import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Grid,
  Hidden,
  Link,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import {
  addDoc,
  arrayUnion,
  collection,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';

import { getIcon, getNegotiationFile, getUserIcon } from '../../../api/aws/s3';
import { db } from '../../../api/firebase/config';
import {
  companyFindByUserId,
  companySnapshot
} from '../../../api/firebase/firestore/company';
import {
  issueSnapshot,
  issueDocument
} from '../../../api/firebase/firestore/issue';
import { mediaSnapshot } from '../../../api/firebase/firestore/media';
import {
  negotiation as negotiationDoc,
  chatOrderByCreated,
  negotiationChat,
  negotiationSnap,
  negotiationsSnapByClientId,
  negotiationOrder
} from '../../../api/firebase/firestore/negotiation';
import {
  userFindByCompanyId,
  userSnapshot
} from '../../../api/firebase/firestore/user';
import {
  sendMailAfterOrderApprovalClient,
  sendNoticeMailFromClientAgent
} from '../../../api/sendgrid';
import {
  black,
  blue,
  blueButton,
  primarycolor
} from '../../../constants/Colors';
import { mypageHeaderTitle, mypageWrapper } from '../../../constants/Location';
import { negotiationStatusText } from '../../../constants/Text';
import {
  chatType,
  negotiationStatus,
  screenType
} from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import { sendSlackAfterOrderApproval } from '../../../modules/slack';
import enbroIconMessage from '../../assets/enbroIconMessage.svg';
import noImageIcon from '../../assets/noImageIcon.svg';
import ApprovalRejectionPanel from '../../uiParts/ApprovalRejectionPanel';
import ConfirmOrderModal from '../../uiParts/ConfirmOrderModal';
import CustomGuideButton from '../../uiParts/CustomGuideButton';
import CustomLoading from '../../uiParts/CustomLoading';
import DeclineModal from '../../uiParts/DeclineModal';
import DeclinePanel from '../../uiParts/DeclinePanel';
import FileUploadModal from '../../uiParts/FileUploadModal';
import RejectionPanel from '../../uiParts/RejectionPanel';
import ShareModal from '../../uiParts/ShareModal';
import Sidebar from '../../uiParts/Sidebar';

import type {
  ChatType,
  NegotiationType
} from '../../../api/firebase/firestore/negotiation/negotiationType';
import type { Dayjs } from 'dayjs';
import type {
  Query,
  Timestamp,
  DocumentData,
  DocumentReference
} from 'firebase/firestore';

const textfieldHeight = (fieldType: string): string => {
  let inputHeight;
  if (fieldType === 'textarea') {
    inputHeight = '128px';
  } else if (fieldType === 'textareaRow10') {
    inputHeight = '280px';
  } else if (fieldType === 'textareaRow9') {
    inputHeight = '250px';
  } else if (fieldType === 'textareaRow8') {
    inputHeight = '220px';
  } else if (fieldType === 'textareaRow7') {
    inputHeight = '190px';
  } else if (fieldType === 'textareaRow6') {
    inputHeight = '160px';
  } else if (fieldType === 'textareaRow5') {
    inputHeight = '140px';
  } else if (fieldType === 'textareaRow4') {
    inputHeight = '120px';
  } else if (fieldType === 'textareaRow3') {
    inputHeight = '88px';
  } else if (fieldType === 'textareaRow2') {
    inputHeight = '64px';
  } else {
    inputHeight = '40px';
  }
  return inputHeight;
};

const styles = {
  addButton: {
    backgroundColor: '#F2F2F2',
    color: '#757575',
    height: '42px',
    mr: '8px',
    width: '42px'
  },
  backButton: { color: blue, ml: '16px', mt: '24px' },
  blueButton,
  card: {
    '@media screen and (max-width:900px)': {
      borderBottom: '1px solid #DBDBE0'
    }
  },
  cardBorder: {
    borderRight: '1px solid #DBDBE0',
    position: 'sticky',
    top: 0
  },
  chat: {
    '@media screen and (max-width:1200px)': {
      maxWidth: '60%'
    },
    bgcolor: 'background.paper',
    borderRadius: '12px 12px 12px 0px',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    color: 'text.primary',
    fontSize: '14px',
    justifyContent: 'flex-start',
    maxWidth: '40ch',
    p: 2,
    whiteSpace: 'pre-wrap',
    width: 'fit-content',
    wordWrap: 'break-word'
  },
  chatFile: {
    bgcolor: 'background.paper',
    borderRadius: '12px 12px 12px 0px',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    color: 'text.primary',
    fontSize: '14px',
    justifyContent: 'flex-end',
    maxWidth: '70%',
    p: 2,
    whiteSpace: 'pre-wrap'
  },
  chatIconCircle: {
    borderRadius: '50%',
    height: '32px',
    minWidth: '32px'
  },
  chatMessages: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
    scrollPaddingBottom: '200px'
  },
  chip: { color: '#757575', p: '0 40px' },
  circle: {
    borderRadius: '50%',
    height: '50px',
    marginBottom: '10px',
    marginRight: '12px',
    minWidth: '50px'
  },
  container: {
    '@media screen and (max-width:960px)': {
      height: 'calc(100vh - 48px)',
      overflow: 'hidden'
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'auto',
    position: 'relative',
    width: '100%'
  },
  declineButton: {
    '&.MuiButton-root:active': {
      border: `1px solid ${black}`
    },
    '&.MuiButton-root:hover': {
      border: `1px solid ${black}`
    },
    border: '1px solid #B3B3B3',
    color: black,
    mb: '16px'
  },
  flexGrow: { flexGrow: 1 },
  header: {
    '@media screen and (max-width:900px)': {
      height: 'unset',
      paddingBottom: '12px'
    },
    alignItems: 'center',
    background: '#FFFFFF',
    borderBottom: '1px solid #DBDBE0',
    display: 'flex',
    height: '88px',
    justifyContent: 'space-between',
    paddingBottom: '24px',
    width: '100%'
  },
  headerGrid: {
    '@media screen and (max-width:900px)': {
      ml: '12px',
      mt: '12px'
    },
    display: 'flex',
    ml: '24px',
    mt: '24px'
  },
  inquiryButton: {
    '@media screen and (max-width:900px)': {
      p: '12px'
    },
    background: '#FFFFFF',
    color: '#808A9F',
    p: '12px 24px'
  },
  list: {
    '@media screen and (max-width:900px)': {
      display: 'none'
    },
    height: '100vh',
    overflow: 'auto',
    width: '100%'
  },
  mainCircle: {
    '@media screen and (max-width:900px)': {
      height: '36px',
      minWidth: '36px'
    },
    borderRadius: '50%',
    height: '50px',
    marginRight: '12px',
    minWidth: '50px'
  },
  meChat: {
    '@media screen and (max-width:1200px)': {
      maxWidth: '60%'
    },
    bgcolor: '#757575',
    borderRadius: '12px 12px 0px 12px',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    color: '#FFFFFF',
    fontSize: '14px',
    justifyContent: 'flex-end',
    maxWidth: '40ch',
    p: 2,
    whiteSpace: 'pre-wrap',
    width: 'fit-content',
    wordWrap: 'break-word'
  },
  meChatFile: {
    bgcolor: 'background.paper',
    borderRadius: '12px 12px 0px 12px',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    color: 'text.primary',
    fontSize: '14px',
    justifyContent: 'flex-end',
    maxWidth: '70%',
    p: 2,
    whiteSpace: 'pre-wrap'
  },
  mediaName: { color: '#757575', fontSize: '14px' },
  messageField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderRadius: '4px 0 0 4px'
      },
      '&.Mui-focused fieldset': {
        borderColor: black
      }
    },
    backgroundColor: '#F2F2F2',
    border: 'none',
    borderRadius: '4px'
  },
  miniCircle: {
    '@media screen and (max-width:900px)': {
      height: '32px',
      minWidth: '32px'
    },
    borderRadius: '50%',
    height: '38px',
    marginRight: '-10px',
    minWidth: '40px'
  },
  mypageHeaderTitle,
  mypageWrapper,
  name: { color: black, fontSize: '16px' },
  panelContainer: {
    '@media screen and (max-width:480px)': {
      p: '16px'
    },
    background: '#FFFFFF',
    bottom: 0,
    display: 'block',
    p: '16px 24px',
    position: 'sticky'
  },
  redCircle: {
    bgcolor: '#BD0017',
    border: '1px solid #FFFFFF',
    borderRadius: '50%',
    height: '14px',
    minWidth: '14px',
    mr: '-12px',
    mt: '-40px',
    zIndex: 1
  },
  search: { margin: '0 16px' },
  searchField: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: black
      }
    },
    backgroundColor: '#F2F2F2',
    mb: '20px',
    mt: '22px'
  },
  sendButton: {
    '&:hover': {
      backgroundColor: '#BD0017',
      color: '#fff',
      opacity: '70%'
    },
    backgroundColor: primarycolor,
    borderRadius: '4px',
    boxShadow: 'none',
    height: '42px',
    ml: '8px',
    width: '75px'
  },
  shareButton: {
    '&:hover': {
      backgroundColor: blue,
      opacity: '70%'
    },
    '@media screen and (max-width:900px)': {
      minWidth: '47px',
      ml: '12px',
      mr: '12px',
      width: '47px'
    },
    backgroundColor: blue,
    boxShadow: 'none',
    fontSize: '12px',
    height: '30px',
    ml: '22px',
    mr: '24px',
    pl: 0,
    pr: 0,
    width: '47px'
  },
  sharePc: {
    '@media screen and (max-width:900px)': {
      display: 'none'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    mt: '24px'
  },
  shareSp: {
    '@media screen and (min-width:900px)': {
      display: 'none'
    },
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
    mt: '24px'
  },
  textCenter: { textAlign: 'center' },
  wholeHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1
  }
};

const ClientMypageMessageDetail: React.FC = () => {
  const { t } = useTranslation();
  const [chatList, setChatList] = useState<
    Array<{
      negotiationId: string;
      mediaName: string;
      name: string;
      issueName: string;
      pImage: string;
      noticeFlg: boolean;
      unauthorized: boolean;
      updated: Timestamp | null;
    }>
  >([]);

  const [searchText, setSearchText] = useState<string>('');

  const filteredChatList = chatList
    .filter(
      (chat) =>
        !chat.unauthorized &&
        ((chat.mediaName?.includes(searchText) ?? false) ||
          (chat.name?.includes(searchText) ?? false) ||
          (chat.issueName?.includes(searchText) ?? false))
    )
    .sort(
      (a, b) =>
        (b.updated ? b.updated.seconds : Date.now()) -
        (a.updated ? a.updated.seconds : Date.now())
    );

  const getNotice = async (
    chatQuery: Query,
    negotiation: NegotiationType
  ): Promise<boolean> => {
    const userId = currentUser?.uid ?? '';
    return await new Promise((resolve) => {
      let notice = false;
      const unsubscribe = onSnapshot(chatQuery, (snapshot) => {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const isRead = data?.isRead as string[] | undefined;
          if (!isRead?.includes(userId)) {
            if (
              negotiation.clientMainUid === userId ||
              (negotiation.clientMember as string[])?.includes(userId)
            ) {
              notice = true;
            }
          }
        });
        resolve(notice);
        unsubscribe();
      });
    });
  };

  const initChatList = async (): Promise<void> => {
    const user = await userSnapshot(currentUser?.uid as string);
    const negotiations = await negotiationsSnapByClientId(
      user.data()?.companyId as string
    );

    try {
      const negotiationsData = await Promise.all(
        negotiations.docs.map(async (negotiation) => {
          let image, issue, media, publisher;
          if (negotiation.data()?.mediaId) {
            media = await mediaSnapshot(negotiation.data()?.mediaId as string);
          }
          if (negotiation.data()?.publisherMainUid) {
            const publisherId = negotiation.data()?.publisherMainUid as string;
            publisher = await userSnapshot(publisherId);
            image = await getUserIcon(publisher);
          }
          if (negotiation.data()?.issueId) {
            issue = await issueSnapshot(negotiation.data()?.issueId as string);
          }
          const chatQuery = query(
            collection(
              db,
              'negotiations',
              negotiation.data()?.negotiationId as string,
              'chats'
            ),
            orderBy('created', 'desc'),
            limit(1)
          );

          async function fetchUpdated(): Promise<Timestamp | null> {
            return await new Promise((resolve, reject) => {
              const unSub = onSnapshot(
                chatQuery,
                (querySnapshot) => {
                  unSub();
                  const docs = querySnapshot.docs.map((document) =>
                    document.data()
                  );
                  if (docs.length > 0) {
                    resolve(docs[0].created as Timestamp);
                  } else {
                    resolve(null);
                  }
                },
                reject
              );
            });
          }

          const updated = await fetchUpdated();

          const notice = await getNotice(
            chatQuery,
            negotiation.data() as NegotiationType
          );
          const unauthorizedFlg = (
            (negotiation.data()?.clientUnauthorizedMember as string[]) ?? []
          )?.includes(currentUser?.uid ?? '');
          return {
            issueName: issue?.data()?.name as string,
            mediaName: media?.data()?.name as string,
            name: publisher?.data()?.name as string,
            negotiationId: negotiation.data()?.negotiationId as string,
            noticeFlg: notice,
            pImage: image as string,
            unauthorized: unauthorizedFlg,
            updated
          };
        })
      );
      setChatList(negotiationsData);
    } catch (e) {
      throwError(e);
    }
  };

  const { id } = useParams<{ id: string }>();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const anchor = queryParams.get('anchor');

  const [chatMessages, setChatMessages] = useState<ChatType[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [negotiation, setNegotiation] = useState<NegotiationType | null>(null);
  const [publisherName, setPublisherName] = useState<string>('');
  const [mediaName, setMediaName] = useState<string>('');
  const [mediaId, setMediaId] = useState<string>('');
  const [publisherImage, setPublisherImage] = useState<string>('');
  const [icons, setIcons] = useState<string[]>([]);

  const init = async (): Promise<void> => {
    if (id) {
      const nego = await negotiationSnap(id);

      if (
        (nego.data()?.clientUnauthorizedMember as string[])?.includes(
          currentUser?.uid ?? ''
        )
      ) {
        navigate('/client/mypage/message');
        return;
      }
      const ownCompany = await companyFindByUserId(currentUser?.uid as string);
      if (
        nego.data() &&
        ownCompany.data()?.companyId === nego.data()?.clientId
      ) {
        setIcons([]);
        setNegotiation(nego.data() as NegotiationType);
        try {
          const mId = nego.data()?.mediaId as string;
          const media = await mediaSnapshot(mId);
          setMediaName(media.data()?.name as string);
          setMediaId(mId);
        } catch {
          setMediaName('');
          setMediaId('');
        }
        try {
          const publisherId = nego.data()?.publisherMainUid as string;
          const publisher = await userSnapshot(publisherId);
          setPublisherName(publisher.data()?.name as string);
        } catch (error) {
          setPublisherName('');
        }

        try {
          const publisherId = nego.data()?.publisherMainUid as string;
          const publisher = await userSnapshot(publisherId);
          const pImage = await getUserIcon(publisher);
          setPublisherImage(pImage);
        } catch (error) {
          setPublisherImage(noImageIcon);
        }

        try {
          const chatsSnapshot = await getDocs(
            query(collection(db, `negotiations/${id ?? ''}/chats`))
          );

          const promises = chatsSnapshot.docs.map(async (chat) => {
            if (
              !(chat.data()?.isRead as string[])?.includes(
                currentUser?.uid ?? ''
              )
            ) {
              await updateDoc(chat.ref, {
                isRead: arrayUnion(currentUser?.uid ?? '')
              });
            }
          });

          await Promise.all(promises);
        } catch (error) {
          throwError(error);
        }

        try {
          const user = await userSnapshot(currentUser?.uid ?? '');
          const usersQuery = await userFindByCompanyId(
            (user.data()?.companyId as string) ?? ''
          );
          await Promise.all(
            usersQuery.map(async (u): Promise<void> => {
              const iconUrl = await getIcon(u);
              if (
                !(nego.data()?.clientUnauthorizedMember as string[])?.includes(
                  (u.data().uid as string) ?? ''
                )
              ) {
                setIcons((prev) => [...prev, iconUrl]);
              }
            })
          );
          const publishersQuery = await userFindByCompanyId(
            (nego.data() as NegotiationType)?.publisherId ?? ''
          );
          await Promise.all(
            publishersQuery.map(async (u): Promise<void> => {
              const iconUrl = await getIcon(u);
              if (
                !(
                  nego.data()?.publisherUnauthorizedMember as string[]
                )?.includes((u.data().uid as string) ?? '')
              ) {
                setIcons((prev) => [...prev, iconUrl]);
              }
            })
          );
        } catch (e) {
          setIcons([]);
        }
      } else {
        navigate('/client/mypage/message');
      }
    }
  };

  const outerElRef = useRef<HTMLDivElement>(null);
  const innerElRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = async (): Promise<void> => {
    if (messageInput === '') {
      return;
    }
    if (negotiation?.negotiationId) {
      const newSubCollectionRef = negotiationChat(negotiation?.negotiationId);
      const newChatDocRef = await addDoc(newSubCollectionRef, {
        chat: messageInput,
        created: new Date(),
        senderId: currentUser?.uid,
        type: chatType.user
      });
      await updateDoc(newChatDocRef, {
        chatId: newChatDocRef.id
      });
      if (!negotiation?.clientMainUid) {
        const nego = negotiationDoc(negotiation?.negotiationId);
        await updateDoc(nego, {
          clientMainUid: currentUser?.uid
        });
      }
    }

    sendNoticeMailFromClientAgent(
      negotiation?.publisherMainUid ?? '',
      negotiation?.publisherMember ?? [],
      messageInput,
      negotiation as NegotiationType,
      'メッセージ',
      currentUser?.uid ?? ''
    );

    setMessageInput('');
    await init();
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await init();
        await initChatList();
        await disableOrderApprove();
        if (outerElRef.current) {
          outerElRef.current.scrollTop = 0; // 外側のスクロール位置を一番上に設定
        }
        if (innerElRef.current) {
          innerElRef.current.scrollTop = innerElRef.current.scrollHeight; // 内側のスクロール位置を一番下に設定
        }
      } catch (e) {
        throwError(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (negotiation?.negotiationId) {
      const chatsCollectionRef = chatOrderByCreated(negotiation?.negotiationId);

      const unsubscribe = onSnapshot(chatsCollectionRef, async (snapshot) => {
        const chatsData = await Promise.all(
          snapshot.docs.map(async (doc) => {
            let image = '';
            if (doc.data().senderId) {
              const user = await userSnapshot(doc.data().senderId as string);
              image = await getUserIcon(user);
            }
            return {
              chat: doc.data().chat as string,
              chatId: doc.data().chatId as string,
              created: doc.data().created as Dayjs,
              fileId: doc.data().fileId as string,
              fileName: doc.data().fileName as string,
              icon: image,
              isRead: doc.data().isRead as [],
              senderId: doc.data().senderId as string,
              systemType: doc.data().systemType as number,
              type: doc.data().type as number
            };
          })
        );
        setChatMessages(chatsData);
      });

      return () => unsubscribe();
    }
  }, [negotiation?.negotiationId]);

  const calcTextAreaHeight = (): number => {
    const rowsNum = messageInput.split('\n').length;
    if (rowsNum > 9) {
      return 9;
    }
    return rowsNum;
  };

  useEffect(() => {
    document.title = t('title.client.messageDetail');
  }, []);

  const [open, setOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const downloadFile = async (filePath: string): Promise<void> => {
    try {
      const signedUrl = await getNegotiationFile(filePath);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = filePath.split('/').pop() ?? filePath;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      throwError(error);
    }
  };

  const proposalDownload = async (): Promise<void> => {
    try {
      const negotiationId = negotiation?.negotiationId as string;
      const q = query(
        collection(db, `negotiations/${negotiationId}/chats`),
        where('type', '==', chatType.proposalFile)
      );
      const querySnapshot = await getDocs(q);
      let i = 0;
      querySnapshot.docs.forEach((doc) => {
        const fileId = doc.data().fileId as string;
        setTimeout(async () => await downloadFile(fileId), i * 2000); // 2秒の遅延を追加
        i++;
      });
    } catch (error) {
      throwError(error);
    }
  };

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);
  const handleDeclineClose = (): void => setDeclineOpen(false);

  const [openShare, setOpenShare] = useState(false);
  const handleOpenShare = (): void => setOpenShare(true);
  const handleCloseShare = (): void => setOpenShare(false);
  const submitPublishOrienSheet = async (): Promise<void> => {
    const issueDoc = issueDocument(negotiation?.issueId as string);
    const issueSnap = await getDoc(issueDoc);
    const applicationDateEnd = (
      issueSnap.data()?.applicationDate as { start: Timestamp; end: Timestamp }
    )?.end;
    await updateDoc(issueDoc, {
      orientationSheetPublished: arrayUnion(negotiation?.publisherId),
      updated: new Date()
    });
    const negotiationDocument = negotiationDoc(
      negotiation?.negotiationId ?? ''
    );
    await updateDoc(negotiationDocument, {
      status: negotiationStatus.proposalPreparation,
      updated: new Date()
    });
    const newSubCollectionRef = negotiationChat(
      negotiation?.negotiationId ?? ''
    );
    let chat: DocumentReference<DocumentData>;
    if (applicationDateEnd) {
      chat = await addDoc(newSubCollectionRef, {
        chat: `オリエンシートを公開して、\n提案が依頼されました。\n提案期限は${dayjs(
          applicationDateEnd.toDate()
        ).format('YYYY/MM/DD')}です`,
        created: new Date(),
        senderId: currentUser?.uid,
        type: chatType.system
      });
    } else {
      chat = await addDoc(newSubCollectionRef, {
        chat: 'オリエンシートを公開して、\n提案が依頼されました。',
        created: new Date(),
        senderId: currentUser?.uid,
        type: chatType.system
      });
    }
    await updateDoc(chat, {
      chatId: chat.id
    });
    const orienUrlChat = await addDoc(newSubCollectionRef, {
      chat: `オリエンシートのURL:\n${
        window.location.origin
      }/main/publisher/mypage/issue/${negotiation?.issueId ?? ''}?from=message`,
      created: new Date(),
      senderId: currentUser?.uid,
      type: chatType.user
    });
    await updateDoc(orienUrlChat, {
      chatId: orienUrlChat.id
    });
    sendNoticeMailFromClientAgent(
      negotiation?.publisherMainUid ?? '',
      negotiation?.publisherMember ?? [],
      `オリエンシートを公開して、提案が依頼されました。\nオリエンシートのURL:\n${
        window.location.origin
      }/main/publisher/mypage/issue/${negotiation?.issueId ?? ''}?from=message`,
      negotiation as NegotiationType,
      'オリエンシート公開',
      currentUser?.uid ?? ''
    );
    await init();
  };
  const submitOrderApproval = async (): Promise<void> => {
    if (negotiation?.negotiationId) {
      const newSubCollectionRef = negotiationChat(negotiation?.negotiationId);
      const chat = await addDoc(newSubCollectionRef, {
        chat: '発注が承認されました',
        created: new Date(),
        senderId: currentUser?.uid,
        type: chatType.system
      });
      await updateDoc(chat, {
        chatId: chat.id
      });
      const nego = negotiationDoc(negotiation?.negotiationId);
      await updateDoc(nego, {
        status: negotiationStatus.orderApproval,
        updated: new Date()
      });
      const negotiationOrderRef = negotiationOrder(negotiation?.negotiationId);
      const negotiationOrderDoc = await getDocs(negotiationOrderRef);
      const issue = await issueSnapshot(negotiation?.issueId);
      const company = await companySnapshot(negotiation?.clientId);
      const publisherCompany = await companySnapshot(negotiation?.publisherId);

      const chatFromMediage = `MediAge運営事務局です。
お支払いの流れについて説明します。

①${
        company.data()?.name as string
      }様がMediAgeへお支払いする(発注承認より5営業日以内にMediAgeより請求書を送付)
②MediAgeが${publisherCompany.data()?.name as string}様へお支払いする(${
        company.data()?.name as string
      }様のお支払いから5営業日以内)

メディアへの振込期日が「${
        negotiationOrderDoc.docs[0].data()?.depositTiming as string
      }」となっております。この5営業日前を振込期日としてMediAgeから${
        company.data()?.name as string
      }様への請求書を発行する予定です。

もしご不明点等がございましたら、[contact@mediage.co.jp]宛にご連絡ください。
※こちらのメッセージに返答しても確認できません`;

      const noticeFromMediage = await addDoc(collection(nego, 'chats'), {
        chat: chatFromMediage,
        created: new Date(),
        senderId: '',
        type: chatType.user
      });

      await updateDoc(noticeFromMediage, {
        chatId: noticeFromMediage.id
      });

      const promises = negotiationOrderDoc.docs.map(async (doc) => {
        const docRef = doc.ref;
        await updateDoc(docRef, {
          approvedDate: new Date()
        });

        sendSlackAfterOrderApproval(
          screenType.client,
          doc.data(),
          negotiation,
          publisherCompany.data()?.name as string,
          issue.data()?.name as string,
          company.data()?.name as string,
          ''
        );
        await sendMailAfterOrderApprovalClient(
          doc.data(),
          negotiation,
          publisherCompany.data()?.name as string,
          issue.data()?.name as string,
          company.data()?.name as string
        );
      });
      await Promise.all(promises);
    }

    sendNoticeMailFromClientAgent(
      negotiation?.publisherMainUid ?? '',
      negotiation?.publisherMember ?? [],
      '発注が承認されました',
      negotiation as NegotiationType,
      '発注承認',
      currentUser?.uid ?? ''
    );

    await init();
  };

  const convertPublisherToClient = (chatMessage: string): string => {
    const urlPattern = `${window.location.origin}/main/publisher/mypage/issue/${
      negotiation?.issueId ?? ''
    }?from=message`;
    let altPattern = urlPattern.replace(
      '/main/publisher/mypage/issue/',
      '/main/client/mypage/issue/edit?type=edit&id='
    );
    altPattern = altPattern.replace('?from=message', '');

    if (chatMessage.includes(urlPattern)) {
      chatMessage = chatMessage.replaceAll(urlPattern, altPattern);
    }

    return chatMessage;
  };

  const [isDisabled, setIsDisabled] = React.useState(false);

  const disableOrderApprove = async (): Promise<void> => {
    if (id) {
      const nego = await negotiationSnap(id);
      if (
        nego.data()?.clientMainUid === currentUser?.uid ||
        ((nego.data()?.clientMember as string[]) ?? []).includes(
          currentUser?.uid ?? ''
        )
      ) {
        setIsDisabled(false);
        return;
      }
      setIsDisabled(true);
    }
  };

  const [loading, setLoading] = React.useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const handleOpenConfirm = (): void => setOpenConfirm(true);
  const handleCloseConfirm = (): void => setOpenConfirm(false);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.client} />
        <Typography component="div" sx={styles.container} ref={outerElRef}>
          <Typography component="div" sx={styles.card}>
            <Grid
              container
              sx={{
                '@media screen and (max-width:900px)': {
                  borderBottom: '1px solid #DBDBE0'
                }
              }}
            >
              <Hidden smDown>
                <Grid item xs={12} md={3}>
                  <Card
                    sx={[
                      styles.cardBorder,
                      {
                        '@media screen and (max-width:900px)': {
                          display: 'none'
                        }
                      }
                    ]}
                  >
                    <Typography>
                      <Button
                        onClick={() => navigate('/client/mypage/message')}
                        sx={styles.backButton}
                      >
                        <KeyboardArrowLeftIcon />
                        {t('message.back')}
                      </Button>
                      <Typography sx={styles.search}>
                        <TextField
                          fullWidth
                          placeholder="検索"
                          sx={styles.searchField}
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          size="small"
                        />
                      </Typography>
                    </Typography>
                    <List sx={styles.list}>
                      {filteredChatList.map((chat, i) => (
                        <ListItem
                          key={i}
                          button
                          selected={id === chat.negotiationId}
                          onClick={() => {
                            navigate(
                              `/client/mypage/message/${
                                chat.negotiationId
                              }?anchor=${id ?? ''}`
                            );
                          }}
                          sx={{
                            borderBottom: '1px solid #DBDBE0',
                            borderTop: i === 0 ? '1px solid #DBDBE0' : 'none'
                          }}
                        >
                          {chat.noticeFlg &&
                            !(chat.negotiationId === id ?? '') &&
                            !(chat.negotiationId === anchor) && (
                              <Box sx={styles.redCircle} />
                            )}
                          <CardMedia
                            sx={styles.circle}
                            image={chat.pImage ?? noImageIcon}
                          />
                          <Typography sx={{ display: 'block' }}>
                            <ListItemText
                              primaryTypographyProps={{
                                color: black,
                                fontSize: '12px'
                              }}
                              primary={chat.mediaName}
                            />
                            <ListItemText
                              primaryTypographyProps={{
                                color: black,
                                fontSize: '14px'
                              }}
                              primary={chat.name}
                            />
                            <ListItemText
                              primaryTypographyProps={{
                                color: '#757575',
                                fontSize: '12px'
                              }}
                              primary={chat.issueName}
                            />
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Card>
                </Grid>
              </Hidden>
              <Grid item xs={12} sm={12} md={9}>
                <Typography sx={styles.wholeHeader}>
                  <Typography sx={styles.header} component="div">
                    <Grid container alignItems={'flex-end'}>
                      <Grid item sx={styles.headerGrid}>
                        <CardMedia
                          sx={styles.mainCircle}
                          image={publisherImage}
                        ></CardMedia>
                        <Typography
                          sx={[styles.mypageHeaderTitle, { display: 'block' }]}
                        >
                          <Typography>
                            <Link
                              sx={[
                                styles.mediaName,
                                { textDecoration: 'none' }
                              ]}
                              href={`/main/client/mypage/media/${mediaId}`}
                              target="_blank"
                            >
                              {mediaName}
                            </Link>
                          </Typography>
                          <Typography sx={styles.name}>
                            {publisherName}
                          </Typography>
                        </Typography>
                      </Grid>
                      <Grid item sx={styles.flexGrow}>
                        <Typography sx={styles.sharePc}>
                          {icons.map((icon, index) => (
                            <CardMedia
                              image={icon}
                              key={index}
                              sx={styles.miniCircle}
                            />
                          ))}
                          <Button
                            variant="contained"
                            size="small"
                            sx={styles.shareButton}
                            onClick={handleOpenShare}
                          >
                            {t('message.share')}
                            <CustomGuideButton
                              title={t('message.share')}
                              guideText={t('guideText.message.share')}
                              color="white"
                            />
                          </Button>
                        </Typography>
                        <Typography sx={styles.shareSp}>
                          {icons.slice(0, 3).map((icon, index) => (
                            <CardMedia
                              image={icon}
                              key={index}
                              sx={styles.miniCircle}
                            />
                          ))}
                          <Typography sx={{ color: '#757575', ml: '12px' }}>
                            {icons.length > 3 ? '+' : ''}
                            {icons.length < 4 ? '' : icons.length - 3}
                          </Typography>
                          <Button
                            variant="contained"
                            size="small"
                            sx={styles.shareButton}
                            onClick={handleOpenShare}
                          >
                            {t('message.share')}
                          </Button>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Typography>
                  <Typography sx={[styles.inquiryButton, { display: 'flex' }]}>
                    <Typography
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        fontSize: '14px'
                      }}
                    >
                      {negotiationStatusText(
                        negotiation?.status ?? 0,
                        screenType.client
                      )}
                    </Typography>
                    <Typography sx={{ flexGrow: 1, textAlign: 'right' }}>
                      {negotiation?.status !== negotiationStatus.inquiries &&
                        negotiation?.status !== negotiationStatus.like &&
                        negotiation?.status !==
                          negotiationStatus.likeApproved &&
                        negotiation?.status !==
                          negotiationStatus.likeRejected &&
                        negotiation?.status !==
                          negotiationStatus.proposalPreparation && (
                          <Button
                            onClick={proposalDownload}
                            sx={{ color: blue, p: 0 }}
                          >
                            {t('message.proposal')}
                          </Button>
                        )}
                      {negotiation?.status !== negotiationStatus.inquiries &&
                        negotiation?.status !== negotiationStatus.like &&
                        negotiation?.status !==
                          negotiationStatus.likeApproved &&
                        negotiation?.status !==
                          negotiationStatus.proposalPreparation &&
                        negotiation?.status !==
                          negotiationStatus.likeRejected &&
                        negotiation?.status !==
                          negotiationStatus.negotiation && (
                          <Button
                            sx={{ color: blue, p: 0 }}
                            onClick={handleOpenConfirm}
                          >
                            {t('message.orderContent')}
                          </Button>
                        )}
                    </Typography>
                  </Typography>
                </Typography>

                <ConfirmOrderModal
                  open={openConfirm}
                  handleClose={handleCloseConfirm}
                />

                <Box ref={innerElRef} sx={styles.chatMessages} p={2}>
                  {chatMessages.map((chatMessage, index) => (
                    <Typography sx={{ mt: '32px' }} key={index}>
                      {chatMessage.type === chatType.system ? (
                        <Typography sx={styles.textCenter}>
                          <Chip
                            label={chatMessage.chat
                              .split('\n')
                              .map((item, i) => (
                                <React.Fragment key={i}>
                                  {item}
                                  <br />
                                </React.Fragment>
                              ))}
                            sx={[
                              styles.chip,
                              {
                                borderRadius: `${
                                  24 * chatMessage.chat.split('\n').length
                                }px`,
                                height: `${
                                  24 * chatMessage.chat.split('\n').length
                                }px`
                              }
                            ]}
                          />
                        </Typography>
                      ) : (
                        <>
                          {chatMessage.type === chatType.file &&
                          chatMessage.senderId === currentUser?.uid ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', ml: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box
                                sx={{
                                  ...styles.meChatFile,
                                  alignItems: 'center',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  zIndex: 'auto'
                                }}
                              >
                                <DescriptionOutlinedIcon />
                                <Typography
                                  component={'div'}
                                  sx={{
                                    ml: '16px',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {chatMessage.fileName}
                                </Typography>
                                <Button
                                  sx={{
                                    '@media screen and (max-width:900px)': {
                                      ml: '10px'
                                    },
                                    backgroundColor: '#F2F2F2',
                                    borderRadius: '50%',
                                    color: '#757575',
                                    height: '32px',
                                    minWidth: 0,
                                    ml: '50px',
                                    padding: 0,
                                    width: '32px',
                                    zIndex: 0
                                  }}
                                  onClick={async () =>
                                    await downloadFile(chatMessage.fileId)
                                  }
                                >
                                  <FileDownloadOutlinedIcon />
                                </Button>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  mr: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : chatMessage.type === chatType.file ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', mr: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box
                                sx={{
                                  ...styles.chatFile,
                                  alignItems: 'center',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  zIndex: 'auto'
                                }}
                              >
                                <DescriptionOutlinedIcon />
                                <Typography
                                  component={'div'}
                                  sx={{
                                    ml: '16px',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {chatMessage.fileName}
                                </Typography>
                                <Button
                                  sx={{
                                    '@media screen and (max-width:900px)': {
                                      ml: '10px'
                                    },
                                    backgroundColor: '#F2F2F2',
                                    borderRadius: '50%',
                                    color: '#757575',
                                    height: '32px',
                                    minWidth: 0,
                                    ml: '50px',
                                    padding: 0,
                                    width: '32px',
                                    zIndex: 0
                                  }}
                                  onClick={async () =>
                                    await downloadFile(chatMessage.fileId)
                                  }
                                >
                                  <FileDownloadOutlinedIcon />
                                </Button>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  ml: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : (
                            ''
                          )}
                          {chatMessage.type === chatType.proposalFile &&
                          chatMessage.senderId === currentUser?.uid ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', ml: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box
                                sx={{
                                  ...styles.meChatFile,
                                  alignItems: 'center',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  zIndex: 'auto'
                                }}
                              >
                                <DescriptionOutlinedIcon />
                                <Typography
                                  component={'div'}
                                  sx={{
                                    ml: '16px',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {chatMessage.fileName}
                                </Typography>
                                <Button
                                  sx={{
                                    '@media screen and (max-width:900px)': {
                                      ml: '10px'
                                    },
                                    backgroundColor: '#F2F2F2',
                                    borderRadius: '50%',
                                    color: '#757575',
                                    height: '32px',
                                    minWidth: 0,
                                    ml: '50px',
                                    padding: 0,
                                    width: '32px',
                                    zIndex: 0
                                  }}
                                  onClick={async () =>
                                    await downloadFile(chatMessage.fileId)
                                  }
                                >
                                  <FileDownloadOutlinedIcon />
                                </Button>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  mr: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : chatMessage.type === chatType.proposalFile ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', mr: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box
                                sx={{
                                  ...styles.chatFile,
                                  alignItems: 'center',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  zIndex: 'auto'
                                }}
                              >
                                <DescriptionOutlinedIcon />
                                <Typography
                                  component={'div'}
                                  sx={{
                                    ml: '16px',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {chatMessage.fileName}
                                </Typography>
                                <Button
                                  sx={{
                                    '@media screen and (max-width:900px)': {
                                      ml: '10px'
                                    },
                                    backgroundColor: '#F2F2F2',
                                    borderRadius: '50%',
                                    color: '#757575',
                                    height: '32px',
                                    minWidth: 0,
                                    ml: '50px',
                                    padding: 0,
                                    width: '32px',
                                    zIndex: 0
                                  }}
                                  onClick={async () =>
                                    await downloadFile(chatMessage.fileId)
                                  }
                                >
                                  <FileDownloadOutlinedIcon />
                                </Button>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  ml: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : (
                            ''
                          )}
                          {chatMessage.senderId === currentUser?.uid &&
                          (chatMessage.type === chatType.user ||
                            chatMessage.type === chatType.contact ||
                            chatMessage.type === chatType.like) ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', ml: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box sx={styles.meChat}>
                                <Linkify>
                                  {convertPublisherToClient(chatMessage.chat)}
                                </Linkify>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  mr: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : chatMessage.type === chatType.user ||
                            chatMessage.type === chatType.contact ||
                            chatMessage.type === chatType.like ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: 2
                              }}
                            >
                              {chatMessage.senderId === '' ? (
                                <CardMedia
                                  sx={[
                                    styles.chatIconCircle,
                                    { alignSelf: 'flex-end', mr: '12px' }
                                  ]}
                                  image={enbroIconMessage}
                                />
                              ) : (
                                <CardMedia
                                  sx={[
                                    styles.chatIconCircle,
                                    { alignSelf: 'flex-end', mr: '12px' }
                                  ]}
                                  image={chatMessage.icon}
                                />
                              )}
                              <Box sx={styles.chat}>
                                <Linkify>
                                  {convertPublisherToClient(chatMessage.chat)}
                                </Linkify>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  ml: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : chatMessage.senderId === currentUser?.uid &&
                            chatMessage.type === chatType.orderDetail ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row-reverse',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', ml: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box sx={styles.meChat}>
                                <Linkify>
                                  {convertPublisherToClient(chatMessage.chat)}
                                </Linkify>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  mr: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : chatMessage.type === chatType.orderDetail ? (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                mb: 2
                              }}
                            >
                              <CardMedia
                                sx={[
                                  styles.chatIconCircle,
                                  { alignSelf: 'flex-end', mr: '12px' }
                                ]}
                                image={chatMessage.icon}
                              />
                              <Box sx={styles.chat}>
                                <Linkify>
                                  {convertPublisherToClient(chatMessage.chat)}
                                </Linkify>
                              </Box>
                              <Typography
                                sx={{
                                  alignSelf: 'flex-end',
                                  color: '#B3B3B3',
                                  fontSize: '12px',
                                  ml: '8px'
                                }}
                              >
                                {dayjs(chatMessage.created?.toDate()).format(
                                  'YYYY/MM/DD HH:mm'
                                )}
                              </Typography>
                            </Box>
                          ) : (
                            ''
                          )}
                        </>
                      )}
                    </Typography>
                  ))}
                  <Box
                    sx={{
                      '@media screen and (max-width:900px)': {
                        height: '60vh'
                      },
                      height: '40vh'
                    }}
                  />
                </Box>
                <Box sx={styles.panelContainer}>
                  {negotiation?.status === negotiationStatus.likeRejected ? (
                    <>
                      <RejectionPanel
                        negotiation={negotiation}
                        currentUser={currentUser}
                        init={init}
                        screen={screenType.client}
                      />
                    </>
                  ) : negotiation?.status === negotiationStatus.like ? (
                    <>
                      <ApprovalRejectionPanel
                        negotiation={negotiation}
                        currentUser={currentUser}
                        init={init}
                        screen={screenType.client}
                      />
                    </>
                  ) : negotiation?.status === negotiationStatus.decline ? (
                    <DeclinePanel screen={screenType.client} />
                  ) : (
                    <>
                      <>
                        {(negotiation?.status === negotiationStatus.inquiries ||
                          negotiation?.status ===
                            negotiationStatus.likeApproved) && (
                          <>
                            <Button
                              variant="outlined"
                              sx={[
                                styles.blueButton,
                                { mb: '16px', mr: '16px' }
                              ]}
                              onClick={submitPublishOrienSheet}
                            >
                              {t('message.publishOrienSheet')}
                            </Button>
                          </>
                        )}
                        {negotiation?.status === negotiationStatus.order && (
                          <>
                            <Button
                              variant="outlined"
                              sx={[
                                styles.blueButton,
                                { mb: '16px', mr: '16px' }
                              ]}
                              onClick={submitOrderApproval}
                              disabled={isDisabled}
                            >
                              {t('message.orderApproval')}
                            </Button>
                          </>
                        )}
                        {(negotiation?.status === negotiationStatus.inquiries ||
                          negotiation?.status === negotiationStatus.like ||
                          negotiation?.status ===
                            negotiationStatus.likeApproved ||
                          negotiation?.status ===
                            negotiationStatus.proposalPreparation ||
                          negotiation?.status ===
                            negotiationStatus.negotiation ||
                          negotiation?.status === negotiationStatus.order) && (
                          <>
                            <Button
                              variant="outlined"
                              sx={styles.declineButton}
                              onClick={() => setDeclineOpen(true)}
                            >
                              {t('message.decline')}
                            </Button>
                          </>
                        )}
                        <Typography
                          sx={{
                            alignItems: 'flex-end',
                            display: 'flex',
                            flexDirection: 'row'
                          }}
                        >
                          <Button sx={styles.addButton} onClick={handleOpen}>
                            <AddIcon />
                          </Button>
                          <Typography
                            sx={{
                              display: 'flex',
                              flexDirection: 'column-reverse',
                              flexGrow: 1,
                              minHeight: '40px'
                            }}
                          >
                            <TextField
                              variant="outlined"
                              multiline
                              rows={calcTextAreaHeight()}
                              maxRows={9}
                              placeholder={t('message.insert')}
                              value={messageInput}
                              onChange={(e) =>
                                setMessageInput(e.currentTarget.value)
                              }
                              sx={[
                                styles.messageField,
                                {
                                  '& .MuiInputBase-input': {
                                    transformOrigin: 'bottom'
                                  },
                                  '& .MuiInputBase-root': {
                                    height: textfieldHeight(
                                      `textareaRow${calcTextAreaHeight()}`
                                    ),
                                    transformOrigin: 'bottom'
                                  }
                                }
                              ]}
                            />
                          </Typography>
                          <Button
                            sx={styles.sendButton}
                            variant="contained"
                            onClick={handleSendMessage}
                          >
                            {t('message.send')}
                          </Button>
                        </Typography>
                      </>
                      <DeclineModal
                        open={declineOpen}
                        handleClose={handleDeclineClose}
                        negotiation={negotiation}
                        currentUser={currentUser}
                        init={init}
                      />
                      <FileUploadModal
                        open={open}
                        handleClose={handleClose}
                        negotiation={negotiation}
                        currentUser={currentUser}
                        screen={screenType.client}
                        setLoading={setLoading}
                      />
                    </>
                  )}
                </Box>
                <ShareModal
                  open={openShare}
                  handleClose={handleCloseShare}
                  currentUser={currentUser}
                  screen={screenType.client}
                  initChatList={initChatList}
                  initMessage={init}
                  disableOrderApprove={disableOrderApprove}
                  setLoading={setLoading}
                />
              </Grid>
            </Grid>
          </Typography>
        </Typography>
      </Typography>
    </>
  );
};

export default ClientMypageMessageDetail;
